import {
    DataTypes,
    DynamicFilterOperations,
    FilterOperations,
} from 'business/definitions';
import { DateFilterTypeEnum, FilterTypeEnum } from './filterTypes.type';
import DateFilterItem from './DateFilterItem';
import SelectorFilterItem from './SelectorFilterItem';
import TreeFilterItem from './TreeFilterItem';
import YearFilterItem from './YearFilterItem';
import DateFilterManager from 'components/SentenceFilter/FilterManagees/DateFilterManager';
import YearFilterManager from 'components/SentenceFilter/FilterManagees/YearFilter/YearFilterManager';
import {
    TreePickerWithUnderline,
    TreePickerWithoutUnderline,
} from 'components/SentenceFilter/FilterManagees/TreePicker';
import SelectorManager from 'components/SentenceFilter/FilterManagees/SelectorManager';
import { isTodayOperation } from 'components/SentenceFilter/FilterManagees/DateManagees/DateOperationSelector/helpers';
import { EXTRACT_OPERATION } from 'business/constants';
import IdentityComponent from 'ui/IdentityComponent/IdentityComponent';
import RangeDatePicker from 'components/SentenceFilter/FilterManagees/DateManagees/RangeDatePicker';
import ExtractDatePicker from 'components/SentenceFilter/FilterManagees/DateManagees/ExtractDatePicker/ExtractDatePicker';
import DynamicDatePicker from 'components/SentenceFilter/FilterManagees/DateManagees/DynamicDatePicker';
import RegularDatePicker from 'components/SentenceFilter/FilterManagees/DateManagees/RegularDatePicker';
import { Typography } from '@mui/material';
import React, { ElementType } from 'react';
import { capitalize } from 'helpers/helpers';
import { translateBlock } from 'i18n';
import { camelize } from 'helpers/syntaxHelper';
import { TFunction } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import NoValueFilterItem from './NoValueFilterItem';
import { valueToOptions } from 'components/SentenceFilter/FilterManagees/DateManagees/ExtractDatePicker/helpers';

// FORCED_TREE_CATEGORIES is used to tell which filter field will be displayed in a modal
// (instead of being displayed in Autocomplete)
// FORCED_TREE_CATEGORIES has no scope options
const FORCED_TREE_CATEGORIES = ['activity.name', 'activity.type', 'place.name'];

export const isFilterTree = (filter: SentenceFilterT): boolean =>
    filter.treeValues != null || FORCED_TREE_CATEGORIES.includes(filter.field);

const getFilterType = (filter: SentenceFilterT): FilterTypeEnum => {
    const dateTypes = [DataTypes.date, DataTypes.datetime];
    const isDateF = dateTypes.includes(filter.type);
    const isCategory = filter.type === DataTypes.category;
    const containOperators = [
        FilterOperations.contains,
        FilterOperations.not_contains,
    ];
    const isContains = containOperators.includes(filter.operation);
    const isYear = filter.type === DataTypes.year;
    const isTree = isFilterTree(filter);

    if (isDateF) return FilterTypeEnum.DATE;
    if (isYear) return FilterTypeEnum.YEAR;
    if (isTree || (isCategory && !isContains)) return FilterTypeEnum.TREE;

    return FilterTypeEnum.SELECTOR;
};

export const getFilterDisplay = (filter: SentenceFilterT): ElementType => {
    if (!filter.value) return NoValueFilterItem;

    const filterType = getFilterType(filter);

    switch (filterType) {
        case FilterTypeEnum.DATE:
            return DateFilterItem;
        case FilterTypeEnum.SELECTOR:
            return SelectorFilterItem;
        case FilterTypeEnum.TREE:
            return TreeFilterItem;
        case FilterTypeEnum.YEAR:
            return YearFilterItem;
        default:
            throw new Error(`Filter type ${filterType} doesn't exist`);
    }
};

export const getFilterManager = (
    filter: SentenceFilterT,
    showUnderline: boolean = true,
): ElementType => {
    const filterType = getFilterType(filter);

    switch (filterType) {
        case FilterTypeEnum.DATE:
            return DateFilterManager;
        case FilterTypeEnum.SELECTOR:
            return SelectorManager;
        case FilterTypeEnum.TREE:
            if (showUnderline) return TreePickerWithUnderline;
            return TreePickerWithoutUnderline;
        case FilterTypeEnum.YEAR:
            return YearFilterManager;
        default:
            throw new Error(`Filter type ${filterType} doesn't exist`);
    }
};

const getDateFilterType = (
    operation: FilterOperationsT | DynamicFilterOperationsT,
    value: string | null,
): DateFilterTypeEnum => {
    const isOperationNull = [
        FilterOperations.is_null,
        FilterOperations.is_not_null,
    ].includes(operation);
    const isTodayOp = value && isTodayOperation(operation, value);
    if (isOperationNull || isTodayOp) return DateFilterTypeEnum.IDENTITY;
    if (operation === 'between') return DateFilterTypeEnum.RANGE;
    if (operation === EXTRACT_OPERATION) return DateFilterTypeEnum.EXTRACT;
    if (Object.keys(DynamicFilterOperations).includes(operation))
        return DateFilterTypeEnum.DYNAMIC;
    return DateFilterTypeEnum.REGULAR;
};

const getTodayOperation = (
    operation: string,
    t: TFunction<'translation', undefined>,
) => {
    return operation === FilterOperations.greater_or_equal
        ? t('sentence:dateOperations.after_today')
        : t('sentence:dateOperations.before_today');
};

export const getDateFilterDisplay = (
    filter: DashboardFilterT & WithScopes,
    t: TFunction<'translation', undefined>,
) => {
    const translatedOperation =
        filter.operation && t(`sentence:dateOperations.${filter.operation}`);
    const translatedScale = t(`sentence:${filter.valueScale}`);
    const dateFilterType = getDateFilterType(filter.operation, filter.value);
    switch (dateFilterType) {
        case DateFilterTypeEnum.IDENTITY: {
            const isTodayOp = isTodayOperation(
                filter.operation,
                filter.value || '',
            );
            return (
                <Typography>
                    {isTodayOp
                        ? getTodayOperation(filter.operation, t)
                        : filter.value}
                </Typography>
            );
        }
        case DateFilterTypeEnum.RANGE: {
            const translatedFilterLinker = t('sentence:filterLinker.andThe');
            const values = filter.value ? filter.value.split('|') : ['', ''];
            return (
                <Typography>{`${translatedOperation} ${new Date(values[0]).toLocaleDateString()} ${translatedFilterLinker} ${new Date(values[1]).toLocaleDateString()}`}</Typography>
            );
        }
        case DateFilterTypeEnum.EXTRACT: {
            const translatedFilterIn = t('sentence:filterLinker.in');
            const translatedFilterThe = t('sentence:filterLinker.the');
            const deserializedValue = filter.value
                ? filter.value
                      .split('|')
                      .map(
                          (value) =>
                              valueToOptions(
                                  value,
                                  filter.valueScale
                                      ? filter.valueScale.toString()
                                      : undefined,
                              ).label,
                      )
                      .join('|')
                : '';

            return (
                <Flex>
                    <Typography marginRight="4px">
                        {`${translatedOperation} ${translatedFilterThe} ${translatedScale} ${translatedFilterIn}`}
                    </Typography>
                    <TreeFilterItem
                        filter={{ ...filter, value: deserializedValue }}
                    />
                </Flex>
            );
        }
        case DateFilterTypeEnum.DYNAMIC: {
            if (filter.value === '1' || filter.operation === 'is_null') {
                const lastScale = `${filter.operation}_${filter.valueScale}`;
                return <Typography>{t(`sentence:${lastScale}`)}</Typography>;
            }
            const translatedFilterInThe = t('sentence:filterLinker.inThe');
            return (
                <Typography>{`${translatedFilterInThe} ${filter.value} ${translatedOperation} ${translatedScale}`}</Typography>
            );
        }
        case DateFilterTypeEnum.REGULAR:
            return (
                <Typography>
                    {filter.value
                        ? `${translatedOperation} ${new Date(filter.value).toLocaleDateString()}`
                        : capitalize(translateBlock(camelize(filter.field)))}
                </Typography>
            );
        default:
            throw new Error(`Date filter type ${dateFilterType} doesn't exist`);
    }
};

export const getDateFilterManager = (
    operation: FilterOperationsT | DynamicFilterOperationsT,
    value: string | null,
): ElementType => {
    const dateFilterType = getDateFilterType(operation, value);
    switch (dateFilterType) {
        case DateFilterTypeEnum.IDENTITY:
            return IdentityComponent;
        case DateFilterTypeEnum.RANGE:
            return RangeDatePicker;
        case DateFilterTypeEnum.EXTRACT:
            return ExtractDatePicker;
        case DateFilterTypeEnum.DYNAMIC:
            return DynamicDatePicker;
        case DateFilterTypeEnum.REGULAR:
            return RegularDatePicker;
        default:
            throw new Error(`Date filter type ${dateFilterType} doesn't exist`);
    }
};
